/**
 * Recover inline emphasis from a pasted HTML table cell. Excel / Sheets / Word
 * mark a bold, italic, underlined, subscript or superscript cell with
 * `style="font-weight:700"` / `vertical-align:sub` or a class pointing at a
 * `<style>` block — never `<b>`/`<i>`/`<u>`/`<sub>`/`<sup>` — so this turns
 * those back into the inline tags the Table cell whitelist keeps.
 *
 * Kept separate from `tableModel` (the grid/span layer). `cellRichText` takes
 * the sanitiser as an argument so there is no import cycle.
 */

interface Emphasis {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  subscript: boolean;
  superscript: boolean;
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function emphasisFromDeclarations(decls: string): Partial<Emphasis> {
  const css = decls.toLowerCase();
  const out: Partial<Emphasis> = {};
  const weight = /font-weight\s*:\s*(bold(?:er)?|[1-9]00)/.exec(css);
  if (weight) {
    const w = weight[1];
    out.bold = w === 'bold' || w === 'bolder' || Number(w) >= 600;
  }
  if (/font-style\s*:\s*italic/.test(css)) out.italic = true;
  if (/text-decoration[^;:]*:[^;]*underline/.test(css)) out.underline = true;
  if (/vertical-align\s*:\s*sub\b/.test(css)) out.subscript = true;
  if (/vertical-align\s*:\s*super\b/.test(css)) out.superscript = true;
  return out;
}

/** Selector -> declaration text, gathered from every `<style>` in the pasted doc. */
export function collectStyleRules(doc: Document): Map<string, string> {
  const rules = new Map<string, string>();
  doc.querySelectorAll('style').forEach((styleEl) => {
    const css = (styleEl.textContent ?? '').replace(/<!--|-->/g, '');
    const re = /([^{}]+)\{([^{}]*)\}/g;
    let match: RegExpExecArray | null = re.exec(css);
    while (match !== null) {
      const decls = match[2];
      match[1].split(',').forEach((selector) => {
        const key = selector.trim();
        if (key) rules.set(key, `${rules.get(key) ?? ''};${decls}`);
      });
      match = re.exec(css);
    }
  });
  return rules;
}

function classDeclarations(el: Element, rules: Map<string, string>): string {
  let out = '';
  el.classList.forEach((cls) => {
    rules.forEach((decls, selector) => {
      if (selector.split(/[\s>]+/).some((t) => t === `.${cls}` || t.endsWith(`.${cls}`))) {
        out += `;${decls}`;
      }
    });
  });
  return out;
}

function elementEmphasis(el: Element, rules: Map<string, string>): Emphasis {
  const emphasis: Emphasis = {
    bold: false,
    italic: false,
    underline: false,
    subscript: false,
    superscript: false,
  };
  const merge = (partial: Partial<Emphasis>) => {
    if (partial.bold !== undefined) emphasis.bold = partial.bold;
    if (partial.italic) emphasis.italic = true;
    if (partial.underline) emphasis.underline = true;
    if (partial.subscript) emphasis.subscript = true;
    if (partial.superscript) emphasis.superscript = true;
  };
  merge(emphasisFromDeclarations(classDeclarations(el, rules)));
  merge(emphasisFromDeclarations(el.getAttribute('style') ?? ''));
  const tag = el.tagName.toLowerCase();
  if (tag === 'b' || tag === 'strong') emphasis.bold = true;
  if (tag === 'i' || tag === 'em') emphasis.italic = true;
  if (tag === 'u') emphasis.underline = true;
  if (tag === 'sub') emphasis.subscript = true;
  if (tag === 'sup') emphasis.superscript = true;
  return emphasis;
}

function wrapEmphasis(html: string, e: Emphasis): string {
  if (!html) return html;
  let out = html;
  if (e.bold) out = `<b>${out}</b>`;
  if (e.italic) out = `<i>${out}</i>`;
  if (e.underline) out = `<u>${out}</u>`;
  if (e.subscript) out = `<sub>${out}</sub>`;
  if (e.superscript) out = `<sup>${out}</sup>`;
  return out;
}

function serializeCellNode(node: Node, rules: Map<string, string>): string {
  if (node.nodeType === Node.TEXT_NODE) return escapeText(node.textContent ?? '');
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (tag === 'br') return '<br>';
  const inner = Array.from(el.childNodes)
    .map((child) => serializeCellNode(child, rules))
    .join('');
  if (tag === 'a') {
    const href = el.getAttribute('href');
    const link = href
      ? `<a href="${escapeText(href).replace(/"/g, '&quot;')}">${inner}</a>`
      : inner;
    return wrapEmphasis(link, elementEmphasis(el, rules));
  }
  return wrapEmphasis(inner, elementEmphasis(el, rules));
}

/** Serialise a `<td>`/`<th>` to whitelist-safe inline HTML, then hand to `sanitize`. */
export function cellRichText(
  td: Element,
  rules: Map<string, string>,
  sanitize: (html: string) => string,
): string {
  const inner = Array.from(td.childNodes)
    .map((child) => serializeCellNode(child, rules))
    .join('');
  return sanitize(wrapEmphasis(inner, elementEmphasis(td, rules)).trim());
}
